/**
 * Application-wide constants
 */

/**
 * Minimum suggested audience size for ad platform integrations (30 * 100)
 */
export const MIN_SUGGESTED_AD_AUDIENCE_SIZE = 3000;

/**
 * Audience size category type.
 * Bucket boundaries and display ranges are returned by GET /segments/config
 * (see useSegmentConfig in composables/segment/useSegmentConfig.ts).
 */
export type AudienceSizeCategory = 'very-narrow' | 'narrow' | 'moderate' | 'broad' | 'too-broad';

/**
 * Ad platform integration slugs that store audience IDs
 */
export const AD_PLATFORM_INTEGRATIONS = [
  'google_ads',
  'meta_ads',
  'reddit_ads',
  'x_ads',
  'bing_ads',
  'snapchat_ads',
  'linkedin_ads',
  'instagram_ads',
  'tiktok_ads',
] as const;

export const NEW_BUILDER_NATIVE_AD_PLATFORM_INTEGRATIONS = [
  'linkedin_ads',
  'google_ads',
  'reddit_ads',
  'meta_ads',
  'x_ads',
  'bing_ads',
] as const;

export const NEW_BUILDER_NATIVE_ADS_WITH_CSV_EXPORT = [
  'csv',
  'google_ads',
  'reddit_ads',
  'meta_ads',
  'x_ads',
  'bing_ads',
  'snapchat_ads',
  'tiktok_ads',
] as const;

/**
 * Integration slugs that are configured elsewhere and don't need segment-level configuration
 * These integrations are excluded from configuration checks in the segment builder
 */
export const EXCLUDED_FROM_CONFIGURATION_CHECKS = [
  'hubspot',
  'salesforce',
  'marketo',
  'pardot',
  'clay',
  'hockey_stack',
  'persana',
] as const;

/**
 */
export const SCHEDULED_CSV_EXPORT_INTEGRATIONS = ['csv'] as const;

/**
 * New builder scheduled CSV export integrations with proper namespacing
 * These use the 'scheduled_csv_' prefix in integration_settings to avoid collisions with native integrations
 */
export const NEW_BUILDER_SCHEDULED_CSV_EXPORT_INTEGRATIONS = [
  'scheduled_csv_csv',
  'scheduled_csv_linkedin_ads',
  'scheduled_csv_google_ads',
  'scheduled_csv_meta_ads',
  'scheduled_csv_reddit_ads',
  'scheduled_csv_x_ads',
  'scheduled_csv_bing_ads',
  'scheduled_csv_snapchat_ads',
  'scheduled_csv_tiktok_ads',
] as const;

/**
 * Base integration slugs for scheduled CSV exports (without scheduled_csv_ prefix)
 * Used for looking up integration metadata and configurations
 */
export const SCHEDULED_CSV_BASE_INTEGRATIONS = [
  'csv',
  'linkedin_ads',
  'google_ads',
  'meta_ads',
  'reddit_ads',
  'x_ads',
  'bing_ads',
  'snapchat_ads',
  'tiktok_ads',
] as const;

/**
 * Day count ranges for audience analysis
 */
export const DAY_COUNT_RANGES = [
  {label: 'Last 7 days', value: 7},
  {label: 'Last 14 days', value: 14},
  {label: 'Last 30 days', value: 30},
  {label: 'Last 3 months', value: 90},
  {label: 'Last 6 months', value: 180},
  {label: 'Last year', value: 365},
] as const;

/**
 * Page size limits for pagination
 */
export const PAGE_LIMITS = [
  {value: 25, label: '25'},
  {value: 50, label: '50'},
  {value: 100, label: '100'},
  {value: 200, label: '200'},
] as const;

/**
 * Schedule frequency options for scheduled exports
 */
export const SCHEDULE_FREQUENCY_OPTIONS = [
  {label: 'Daily', value: 'daily'},
  {label: 'Weekly', value: 'weekly'},
  {label: 'Monthly', value: 'monthly'},
] as const;

export const AD_PLATFORMS = [
  {
    label: 'LinkedIn',
    value: 'LINKEDIN',
    integrationSlug: 'linkedin',
    icon: '/integrations/linkedin.png',
    href: 'https://www.linkedin.com/campaignmanager',
    supportsBidAgent: true,
  },
  {
    label: 'Google',
    value: 'GOOGLE',
    integrationSlug: 'google_ads',
    icon: '/integrations/google_ads.svg',
    href: 'https://ads.google.com/aw/campaigns',
  },
  {
    label: 'Meta',
    value: 'META',
    icon: '/integrations/meta.svg',
    href: 'https://adsmanager.facebook.com/adsmanager',
  },
  {
    label: 'TikTok',
    value: 'TIKTOK',
    icon: '/integrations/tiktok.svg',
    href: 'https://ads.tiktok.com',
  },
  {
    label: 'YouTube',
    value: 'YOUTUBE',
    icon: '/integrations/youtube.png',
    href: 'https://ads.google.com/aw/campaigns',
  },
  {label: 'Bing', value: 'BING', icon: '/integrations/bing.png', href: 'https://ads.microsoft.com'},
  {
    label: 'Snapchat',
    value: 'SNAPCHAT',
    icon: '/integrations/snapchat.svg',
    href: 'https://ads.snapchat.com',
  },
  {
    label: 'Reddit',
    value: 'REDDIT',
    icon: '/integrations/reddit.svg',
    href: 'https://ads.reddit.com',
  },
  {
    label: 'Pinterest',
    value: 'PINTEREST',
    icon: '/integrations/pinterest.png',
    href: 'https://ads.pinterest.com',
  },
  {label: 'X', value: 'X', icon: '/integrations/x.png', href: 'https://ads.x.com'},
];

/**
 * Human-readable labels for the click metric shown per ad platform.
 * These are the names each platform's Ads Manager uses for clicks sent to the landing page.
 */
export const CLICKS_METRIC_LABELS: Record<string, string> = {
  LINKEDIN: 'Clicks to Landing Page',
  GOOGLE: 'Clicks',
  META: 'Landing Page Views',
  TIKTOK: 'Link Clicks',
  YOUTUBE: 'Clicks',
  BING: 'Clicks',
  SNAPCHAT: 'Website Clicks',
  REDDIT: 'Link Clicks',
  PINTEREST: 'Outbound Clicks',
  X: 'Link Clicks',
};

export const DEFAULT_DATE_RANGES = [
  {label: 'Last 7 days', duration: {days: 7}},
  {label: 'Last 14 days', duration: {days: 14}},
  {label: 'Last 30 days', duration: {days: 30}},
  {label: 'Last 3 months', duration: {months: 3}},
  {label: 'Last 6 months', duration: {months: 6}},
  {label: 'Last year', duration: {years: 1}},
] as const;

export const VISITOR_FEED_DATE_RANGES = [
  {label: 'Last 7 days', duration: {days: 7}},
  {label: 'Last 14 days', duration: {days: 14}},
  {label: 'Last 30 days', duration: {days: 30}},
  {label: 'Last 3 months', duration: {months: 3}},
] as const;

export const CAMPAIGN_REPORTING_DATE_RANGES = [
  {label: 'Last 7 days', duration: {days: 7}},
  {label: 'Last 14 days', duration: {days: 14}},
  {label: 'Last 30 days', duration: {days: 30}},
  {label: 'Last 3 months', duration: {months: 3}},
] as const;

/**
 * Returns the tooltip message for when a list's remote (CRM) size differs from Vector's size.
 * Used in segment contact lists and account lists.
 */
export function getRemoteSizeTooltipMessage(remoteSize: number): string {
  return `The true size on your CRM is ${remoteSize.toLocaleString()}. It may be lower in Vector because we remove records that don't meet our requirements (e.g., contacts without email or company, accounts without domain).`;
}
